import defaults from 'lodash/defaults';
import get from 'lodash/get';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import {isExternalUrl} from 'privates/urlUtils';
import reporter from 'privates/reporter';
import {generateError} from 'privates/validations/errors';
import assign from 'lodash/assign';
import linkGenerator from 'privates/utils/linkGenerator';


import {validate, validateByType, TYPES} from './../validations/validation';

const DEPRECATED_MEDIA_SRC_REGEX = /^image:\/\/v1\/([^\/]+)\/([0-9]+)_([0-9]+)\/(.*)/;
const MEDIA_SRC_REGEX = /^wix:image:\/\/v1\/([^\/]+)\/([^\/]+)#originWidth=([0-9]+)&originHeight=([0-9]+)(.*)/;

function buildFullUrl(src, width, height, title) {
    if (MEDIA_SRC_REGEX.test(src) || DEPRECATED_MEDIA_SRC_REGEX.test(src)) {
        return src;
    }
    return buildFullUrlFromUri(src, width, height, title);
}

function isValidImage(src) {
    return MEDIA_SRC_REGEX.test(src) || DEPRECATED_MEDIA_SRC_REGEX.test(src) || validateByType(src, TYPES.URL);
}

function extractModelDataFromSrc(src) {
    if (MEDIA_SRC_REGEX.test(src)) {
        const [, uri, filename, width, height] = src.match(MEDIA_SRC_REGEX) || [0, src];

        const title = convertFilenameToTitle(filename);
        return {uri, width: parseInt(width, 10), height: parseInt(height, 10), title};
    }

    if (DEPRECATED_MEDIA_SRC_REGEX.test(src)) {
        const [, uri, width, height, title] = src.match(DEPRECATED_MEDIA_SRC_REGEX) || [0, src];
        return {uri, width: parseInt(width, 10), height: parseInt(height, 10), title};
    }

    if (validateByType(src, TYPES.URL)) {
        return {uri: src};
    }

    return null;
}

function buildFullUrlFromMediaRef(mediaRef) {
    return buildFullUrlFromUri(mediaRef.uri, mediaRef.width, mediaRef.height, mediaRef.title);
}

function convertFilenameToTitle(filename) {
    const decodedFilename = decodeURI(filename);
    return decodedFilename.startsWith('_.') ? '' : decodedFilename;
}

function convertTitleToFilename(title, extension) {
    if (!title) {
        return `_.${extension}`;
    }

    const titleHasExtension = title.split('.').length === 2;

    if (!titleHasExtension) {
        return `${title}.${extension}`;
    }

    return title;
}

function convertImageToModelFormatAndReportIfError(src, propertyName, functionName, index) {
    const imageData = extractModelDataFromSrc(src);
    assign(imageData, {type: 'Image'});
    if (!imageData) {
        const error = !isNil(index) ? generateError.error_bad_image_format_with_index : generateError.error_bad_image_format;
        reporter.reportSdkError(error({
            propertyName,
            functionName,
            index,
            wrongValue: src
        }));
        return null;
    }
    return imageData;
}

function buildFullUrlFromUri(uri, width, height, title) {
    if (!uri) {
        return null;
    }

    if (isExternalUrl(uri)) {
        return uri;
    }

    const extension = uri.split('.').pop();
    const filename = encodeURI(convertTitleToFilename(title, extension));

    if (!isNaN(height) && !isNaN(width)) {
        return `wix:image://v1/${uri}/${filename}#originWidth=${width}&originHeight=${height}`;
    }

    return uri;
}

function convertModelToUserFormat(imageData) {
    const height = isUndefined(imageData.height) ? imageData.height : Number(imageData.height);
    const width = isUndefined(imageData.width) ? imageData.width : Number(imageData.width);
    const title = imageData.title;
    const src = buildFullUrl(imageData.uri || imageData.src, width, height, title);
    const result = omitBy({
        description: imageData.description,
        alt: imageData.alt,
        title,
        height,
        width,
        link: linkGenerator.convertLinkToUrlAndReportIfError(imageData.link, imageData.target, 'items'),
        target: get(imageData, 'link.target')
    }, isUndefined);

    result.src = src;

    return result;
}

function convertUserToModelFormat(imageData, imageIndex) {
    const uri = imageData.uri || imageData.src;
    let dataFromSrc = {};

    if (isNil(uri)) {
        dataFromSrc.uri = uri;
    } else {
        dataFromSrc = convertImageToModelFormatAndReportIfError(uri, 'src', 'items', imageIndex);
        if (!dataFromSrc) {
            dataFromSrc = {uri: ''};
        }
    }

    const result = omitBy(
        defaults({
            type: 'Image',
            description: imageData.description,
            title: imageData.title,
            alt: imageData.alt,
            id: imageData.id,
            link: linkGenerator.convertLinkToModelFormatAndReportIfError(imageData.link, imageData.target, 'items', imageIndex),
            width: imageData.width,
            height: imageData.height
        }, dataFromSrc),
        isUndefined);

    result.uri = dataFromSrc.uri;

    return result;
}

function checkImageValidation(imageData, imageIndex) {
    const imageType = 'Image';
    return validate(imageType, 'items', imageData, true, imageIndex);
}

export default {
    isValidImage,
    buildFullUrl,
    buildFullUrlFromMediaRef,
    extractModelDataFromSrc,
    buildFullUrlFromUri,
    checkImageValidation,
    convertImageToModelFormatAndReportIfError,
    dataFormat: {
        toUser: convertModelToUserFormat,
        toModel: convertUserToModelFormat
    }
};



// WEBPACK FOOTER //
// ./js/modules/privates/utils/imageUtils.es6